// Forma "legada" de um e-mail — espelho de isugar-api/functions/utils/legacyEmail.js.
//
// Até out/2026 a API criava as contas com o e-mail reescrito pelo
// `normalizeEmail()` do validator: sem os pontos e o "+tag" do Gmail, sem o
// "+tag" de Outlook/iCloud, sem o "-tag" do Yahoo. O app entrava com o e-mail
// digitado e o login falhava para sempre. O login usa esta função para tentar
// a forma antiga quando a digitada não é aceita.
//
// Reproduz as opções padrão do validator 13.15; as listas de domínio são as
// dele.

const ICLOUD = ["icloud.com", "me.com"];
const YAHOO = [
  "rocketmail.com",
  "yahoo.ca",
  "yahoo.co.uk",
  "yahoo.com",
  "yahoo.de",
  "yahoo.fr",
  "yahoo.in",
  "yahoo.it",
  "ymail.com",
];
const YANDEX = ["yandex.ru", "yandex.ua", "yandex.kz", "yandex.com", "yandex.by", "ya.ru"];
const OUTLOOK = [
  "hotmail.at", "hotmail.be", "hotmail.ca", "hotmail.cl", "hotmail.co.il",
  "hotmail.co.nz", "hotmail.co.th", "hotmail.co.uk", "hotmail.com",
  "hotmail.com.ar", "hotmail.com.au", "hotmail.com.br", "hotmail.com.gr",
  "hotmail.com.mx", "hotmail.com.pe", "hotmail.com.tr", "hotmail.com.vn",
  "hotmail.cz", "hotmail.de", "hotmail.dk", "hotmail.es", "hotmail.fr",
  "hotmail.hu", "hotmail.id", "hotmail.ie", "hotmail.in", "hotmail.it",
  "hotmail.jp", "hotmail.kr", "hotmail.lv", "hotmail.my", "hotmail.ph",
  "hotmail.pt", "hotmail.sa", "hotmail.sg", "hotmail.sk", "live.be",
  "live.co.uk", "live.com", "live.com.ar", "live.com.mx", "live.de", "live.es",
  "live.eu", "live.fr", "live.it", "live.nl", "msn.com", "outlook.at",
  "outlook.be", "outlook.cl", "outlook.co.il", "outlook.co.nz",
  "outlook.co.th", "outlook.com", "outlook.com.ar", "outlook.com.au",
  "outlook.com.br", "outlook.com.gr", "outlook.com.pe", "outlook.com.tr",
  "outlook.com.vn", "outlook.cz", "outlook.de", "outlook.dk", "outlook.es",
  "outlook.fr", "outlook.hu", "outlook.id", "outlook.ie", "outlook.in",
  "outlook.it", "outlook.jp", "outlook.kr", "outlook.lv", "outlook.my",
  "outlook.ph", "outlook.pt", "outlook.sa", "outlook.sg", "outlook.sk",
  "passport.com",
];

const legacyLocalAndDomain = (local, domain) => {
  if (domain === "gmail.com" || domain === "googlemail.com") {
    // Pontos consecutivos ("a..b") o validator mantém.
    const withoutTag = local.split("+")[0].replace(/\.+/g, (m) => (m.length > 1 ? m : ""));
    return [withoutTag, "gmail.com"];
  }
  if (ICLOUD.includes(domain) || OUTLOOK.includes(domain)) {
    return [local.split("+")[0], domain];
  }
  if (YAHOO.includes(domain)) {
    const parts = local.split("-");
    return [parts.length > 1 ? parts.slice(0, -1).join("-") : parts[0], domain];
  }
  if (YANDEX.includes(domain)) {
    return [local, "yandex.ru"];
  }
  return [local, domain];
};

/** Forma antiga do e-mail, ou null quando ela é igual à digitada. */
export const legacyEmailForm = (email) => {
  if (typeof email !== "string") return null;
  const current = email.trim().toLowerCase();
  const at = current.lastIndexOf("@");
  if (at <= 0) return null;
  const [local, domain] = legacyLocalAndDomain(current.slice(0, at), current.slice(at + 1));
  if (!local) return null;
  const legacy = `${local}@${domain}`;
  return legacy !== current ? legacy : null;
};

// Erros de login que podem ser só o e-mail no formato antigo.
const LEGACY_RETRY_CODES = new Set([
  "auth/invalid-credential",
  "auth/invalid-login-credentials",
  "auth/user-not-found",
  "auth/wrong-password",
]);

/**
 * Entra com o e-mail digitado e, se a credencial for recusada, tenta a forma
 * antiga. Se a segunda também recusar a credencial, sobe o erro da primeira;
 * qualquer outro motivo da segunda (conta banida, muitas tentativas, rede) é
 * o que o usuário precisa ver.
 *
 * @param {(email: string, password: string) => Promise<any>} signIn
 */
export const signInAcceptingLegacyEmail = async (signIn, email, password) => {
  try {
    return await signIn(email, password);
  } catch (error) {
    const legacy = legacyEmailForm(email);
    if (!legacy || !LEGACY_RETRY_CODES.has(error?.code)) throw error;
    try {
      return await signIn(legacy, password);
    } catch (legacyError) {
      throw LEGACY_RETRY_CODES.has(legacyError?.code) ? error : legacyError;
    }
  }
};

export default legacyEmailForm;
